import { logger } from '@/lib/logger'

import { useCallback } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Hook to check if any builder has unsaved changes by reading localStorage directly.
 * This avoids the need to import the builder store in components that don't use it.
 *
 * Handles both builders:
 * - Digital builder: /builder → checks dday-builder-storage
 * - Stationery builder: /stationery-builder → checks dday-stationery-builder-storage
 *
 * IMPORTANT: Only checks for unsaved changes when currently on a builder page.
 * This prevents false warnings when navigating from dashboard with stale localStorage.
 */
export function useBuilderUnsavedCheck() {
  const pathname = usePathname()

  const hasUnsavedBuilderChanges = useCallback(() => {
    try {
      // Determine which builder we're on (use startsWith to avoid cross-matching)
      const isOnDigitalBuilder =
        pathname.startsWith('/builder') ||
        pathname.startsWith('/en/builder') ||
        pathname.startsWith('/ko/builder')
      const isOnStationeryBuilder =
        pathname.startsWith('/stationery-builder') ||
        pathname.startsWith('/en/stationery-builder') ||
        pathname.startsWith('/ko/stationery-builder')

      // Check digital builder unsaved changes
      if (isOnDigitalBuilder) {
        const builderStorage = localStorage.getItem('dday-builder-storage')
        if (builderStorage) {
          const parsed = JSON.parse(builderStorage)
          if (parsed?.state?.isSaved === false) return true
        }
      }

      // Check stationery builder unsaved changes
      if (isOnStationeryBuilder) {
        const stationeryStorage = localStorage.getItem(
          'dday-stationery-builder-storage'
        )
        if (stationeryStorage) {
          const parsed = JSON.parse(stationeryStorage)
          if (parsed?.state?.isSaved === false) return true
        }
      }

      return false
    } catch (error) {
      logger.error('Error checking builder unsaved changes:', error)
      return false
    }
  }, [pathname])

  return { hasUnsavedBuilderChanges }
}
